import { type AnimationOptions, motion, stagger, useAnimate } from 'motion/react'
import { useState } from 'react'

interface LetterSwapProps {
	label: string
	reverse?: boolean
	transition?: AnimationOptions
	staggerDuration?: number
	staggerFrom?: 'first' | 'last' | 'center' | number
	className?: string
}

export function LetterSwapForward({
	label,
	reverse = true,
	transition = {
		type: 'spring',
		duration: 0.7,
	},
	staggerDuration = 0.03,
	staggerFrom = 'first',
	className,
}: LetterSwapProps) {
	const [scope, animate] = useAnimate()
	const [blocked, setBlocked] = useState(false)

	const hoverStart = () => {
		if (blocked) return

		setBlocked(true)

		const mergeTransition = (baseTransition: AnimationOptions) => ({
			...baseTransition,
			delay: stagger(staggerDuration, {
				from: staggerFrom,
			}),
		})

		void animate(
			'.letter',
			{ y: reverse ? '100%' : '-100%' },
			mergeTransition(transition),
		).then(() => {
			void animate(
				'.letter',
				{ y: 0 },
				{ duration: 0 },
			).then(() => {
				setBlocked(false)
			})
		})

		void animate(
			'.letter-secondary',
			{ top: '0%' },
			mergeTransition(transition),
		).then(() => {
			void animate(
				'.letter-secondary',
				{ top: reverse ? '-100%' : '100%' },
				{ duration: 0 },
			)
		})
	}

	return (
		<span
			className={`inline-flex items-center relative overflow-hidden ${className ?? ''}`}
			onMouseEnter={hoverStart}
			ref={scope}
		>
			<span className="sr-only">{label}</span>

			{label.split('').map((letter: string, i: number) => (
				<span
					className="whitespace-pre relative flex"
					key={i}
					aria-hidden={true}
				>
					<motion.span className="relative letter" style={{ top: 0 }}>
						{letter}
					</motion.span>
					<motion.span
						className="absolute letter-secondary"
						style={{ top: reverse ? '-100%' : '100%' }}
					>
						{letter}
					</motion.span>
				</span>
			))}
		</span>
	)
}
